import { EventEmitter } from '@apparel/core/events';
import { Log } from '@apparel/core/log';
import { hash } from '@apparel/core/utils/hash';
import { buildMessage } from '@packages/exceptions/buildMessage';
import { AxiosError } from 'axios';
import { Set } from 'immutable';
import { OptionsObject, ProviderContext, VariantType } from 'notistack';

export type NotifyKey = string | number | undefined | null;

export enum NotificationEvents {
    CHANGE = 'notification@change',
}

export interface NotificationServiceProviderImpl {
    push(variant: VariantType, message: string, options?: OptionsObject): Promise<NotifyKey>;

    info(message: string, options?: OptionsObject): Promise<NotifyKey>;

    success(message: string, options?: OptionsObject): Promise<NotifyKey>;

    warning(message: string, options?: OptionsObject): Promise<NotifyKey>;

    error(message: string, options?: OptionsObject): Promise<NotifyKey>;

    close(key?: string): void;
}

export class NotificationServiceProvider implements NotificationServiceProviderImpl {
    public engine!: ProviderContext;
    private openedNotifications: Set<NotifyKey> = Set();
    private logger = Log.channel('NotificationServiceProvider');
    private listener: EventEmitter<NotificationEvents>;

    constructor() {
        this.listener = new EventEmitter<NotificationEvents>();
    }

    public async push(variant: VariantType, message: string, options: OptionsObject = {}): Promise<NotifyKey> {
        if (!this.engine) {
            return new Promise((resolve) => {
                setTimeout(async () => {
                    const key = await this.push(variant, message, options);
                    resolve(key);
                }, 100);
            });
        }

        if (message !== 'string') {
            message = buildMessage(message);
        }

        const key = options.key || this._generateMessageKey(message);

        return this.engine.enqueueSnackbar(message, {
            ...options,
            variant,
            key,
            autoHideDuration: options.autoHideDuration ?? 3000,
            persist: options.persist ?? false,

            onEnter: () => {
                this.openedNotifications = this.openedNotifications.add(key);
                this.listener.emit(NotificationEvents.CHANGE);
            },

            onExit: () => {
                this.openedNotifications = this.openedNotifications.remove(key);
                this.listener.emit(NotificationEvents.CHANGE);
            },
        });
    }

    public info(message: string, options: OptionsObject = {}): Promise<NotifyKey> {
        return this.push('info', buildMessage(message), options);
    }

    public success(message: string, options: OptionsObject = {}): Promise<NotifyKey> {
        return this.push('success', buildMessage(message), options);
    }

    public warning(message: string, options: OptionsObject = {}): Promise<NotifyKey> {
        return this.push('warning', buildMessage(message), options);
    }

    public error(message: Error | AxiosError | string, options: OptionsObject = {}): Promise<NotifyKey> {
        this.logger.error('Error pushed', { message, options });
        return this.push('error', buildMessage(message), options);
    }

    public close(key?: string): void {
        if (this.engine) {
            this.engine.closeSnackbar(key);
        }
    }

    public on(event: NotificationEvents, func: any) {
        this.listener.addEventListener(event, func);
    }

    public countOpened() {
        return this.openedNotifications.size;
    }

    /**
     * Generate an id from a "Message like this" to a "message-like-this"
     * @param str
     * @returns {string}
     * @private
     */
    private _generateMessageKey(str: string): string {
        return `${new Date().getTime()}-${hash(str)}`.trim();
    }
}
